console.debug("[BOOT 1] main.tsx top of file executing");

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
// tasks.md #29: the old global `import "./index.css"` shipped every route's
// Tailwind utilities in one eager, render-blocking bundle. Replaced with the
// tiny "shell" build (just App.tsx/main.tsx's own chrome — the auth spinner
// + ChunkErrorBoundary fallback); each lazy route now imports its own group
// stylesheet (client/src/styles/public.css or app.css) instead.
import "./styles/shell.css";
import App from "./App";
import { initPlausible } from "@/lib/plausible";
import { installPreviewFetch } from "@/lib/preview";

// TIMING (task #106 FOUC diagnosis): how long between HTML parse and this
// module executing = JS download + Vite transform + module graph evaluation.
const jsExecutionStart = performance.now();
console.log(
  "[timing] main.tsx executing:",
  Math.round(jsExecutionStart) + "ms",
  "(delta from html parse:",
  Math.round(jsExecutionStart - ((window as any).__tbTiming?.htmlParse ?? 0)) + "ms)",
);

// DEV-only local UI preview harness (mock auth + API fixtures). No-op unless
// ?preview=1 is set and this is a dev build. Must run before the app renders.
installPreviewFetch();

// Self-hosted Plausible analytics (runs alongside GA4/GTM). No-ops if unconfigured.
initPlausible();

const rootEl = document.getElementById("root");
console.debug("[BOOT 4] #root element lookup:", rootEl ? "FOUND" : "NULL");

try {
  console.log("[timing] React render start:", Math.round(performance.now()) + "ms");
  createRoot(rootEl!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
  console.debug("[BOOT 5] createRoot().render() returned (React mount initiated)");
} catch (err) {
  console.debug("[BOOT 5 ERROR] createRoot/render threw:", String(err));
}

function isChunkLoadError(msg: string, errLike?: unknown): boolean {
  if (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("dynamically imported module")
  ) {
    return true;
  }
  const name = (errLike as { name?: string } | undefined)?.name;
  return name === "ChunkLoadError";
}

function maybeReloadOnce(): void {
  const KEY = "tb_chunk_reload_at";
  try {
    const parsed = Number(sessionStorage.getItem(KEY));
    const last = Number.isFinite(parsed) ? parsed : 0;
    if (Date.now() - last > 10_000) {
      sessionStorage.setItem(KEY, String(Date.now()));
      console.debug("[CHUNK AUTO-RELOAD] triggering window.location.reload()");
      window.location.reload();
    } else {
      console.debug("[CHUNK AUTO-RELOAD] suppressed (cooldown active)");
    }
  } catch {
    /* sessionStorage unavailable */
  }
}

window.addEventListener(
  "error",
  (e) => {
    const msg = String(e.message || e.error || "");
    console.debug("[BOOT WINDOW ERROR]", msg, e.filename, e.lineno);
    if (isChunkLoadError(msg, e.error)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      maybeReloadOnce();
    }
  },
  true,
);

window.addEventListener(
  "unhandledrejection",
  (e) => {
    const reason = e.reason as { message?: string; name?: string } | undefined;
    const msg = String(reason?.message || e.reason || "");
    console.debug("[BOOT UNHANDLED REJECTION]", msg);
    if (isChunkLoadError(msg, e.reason)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      maybeReloadOnce();
    }
  },
  true,
);

// Sentry is deferred until well after first paint: its browser-tracing +
// session-replay bundles are the single biggest chunk on the page (417 KiB,
// PageSpeed 2026-08-22) and were part of the blocking module graph delaying
// React mount (the unstyled-SSR-content flash). Errors thrown in the first
// idle moment are a fair trade for a much faster first render.
//
// `requestIdleCallback` alone is NOT enough (tasks.md #42, found 2026-08-22):
// it only protects the MAIN THREAD, not the network. TBT measures 10ms on
// this page — the thread goes idle almost immediately regardless of network
// speed, so the idle callback fired right away and the 417 KiB chunk still
// downloaded in the same critical window as the actual page content on a
// throttled connection (confirmed in PageSpeed's network dependency tree:
// Sentry's chunk + its own googleapis.com/sentry.io calls were loading
// BEFORE home.tsx's own 11 KiB chunk even started fetching). Gating on
// `window.load` instead ties the delay to how long the page actually took
// to finish loading its real resources — slow network means a later
// `load` event means Sentry waits longer, which is exactly the behavior
// we want. The idle-callback usage below is now nested inside that gate
// purely for its original main-thread-friendliness, not as the network gate.
const initSentry = async () => {
  const Sentry = await import("@sentry/react");
  Sentry.init({
    dsn: "https://f93ba7a4a4de529de4ebe9d5810c76e4@o4509578920394752.ingest.us.sentry.io/4510376312045568",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.replit\.app/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
  console.log("[timing] Sentry initialized (deferred):", Math.round(performance.now()) + "ms");
};
function scheduleSentryInit(): void {
  const SENTRY_POST_LOAD_DELAY_MS = 4000;
  const runSoon = () => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => void initSentry(), { timeout: 2000 });
    } else {
      void initSentry();
    }
  };
  const runAfterLoad = () => setTimeout(runSoon, SENTRY_POST_LOAD_DELAY_MS);
  if (document.readyState === "complete") {
    runAfterLoad();
  } else {
    window.addEventListener("load", runAfterLoad, { once: true });
  }
}
scheduleSentryInit();
